import posthog from 'posthog-js';
import { useCallback, useEffect, useSyncExternalStore } from 'react';

import storage from '~/modules/utils/storage';
import useRemoteMicName from '~/routes/remote-mic/hooks/use-remote-mic-name';

// Saved per singer rather than per phone, so whoever picks the phone up next has their own list.
const KEY = 'remote-mic-saved-songs-by-singer';
// Where the list lived when it belonged to the phone. Every singer gets a copy the first time they use
// the phone, and the singers who have had theirs are remembered so a song they took out stays out.
const LEGACY_KEY = 'remote-mic-saved-songs';
const LEGACY_GIVEN_KEY = 'remote-mic-saved-songs-given-to';

type Lists = Record<string, string[]>;

const NO_SONGS: string[] = [];
const listeners = new Set<() => void>();

// One store for every copy of the hook - the song list and the shell's sync both read it - kept as
// the parsed copy of whatever storage holds, so each reader always sees the same lists.
let cachedRaw: string | null | undefined;
let cachedLists: Lists = Object.create(null);

// Keyed by whatever a singer is called, so with no prototype: a singer named "constructor" would
// otherwise read `Object` back as their list.
const parseLists = (raw: string | null): Lists => {
  const lists: Lists = Object.create(null);
  try {
    const parsed: unknown = raw ? JSON.parse(raw) : {};
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return lists;
    for (const [singer, list] of Object.entries(parsed)) {
      if (Array.isArray(list)) lists[singer] = list.filter((id): id is string => typeof id === 'string');
    }
  } catch {
    // Not JSON: starts empty.
  }
  return lists;
};

const readLists = (): Lists => {
  const raw = global.localStorage?.getItem(KEY) ?? null;
  if (raw !== cachedRaw) {
    cachedRaw = raw;
    cachedLists = parseLists(raw);
  }
  return cachedLists;
};

const writeLists = (lists: Lists) => {
  storage.local.setItem(KEY, lists);
  listeners.forEach((listener) => listener());
};

const subscribe = (listener: () => void) => {
  const fromAnotherTab = (event: StorageEvent) => event.key === KEY && listener();
  listeners.add(listener);
  global.addEventListener?.('storage', fromAnotherTab);

  return () => {
    listeners.delete(listener);
    global.removeEventListener?.('storage', fromAnotherTab);
  };
};

const takeLegacyList = (singer: string) => {
  const legacy = storage.local.getItem<string[]>(LEGACY_KEY);
  if (!Array.isArray(legacy)) return [];

  const given = storage.local.getItem<string[]>(LEGACY_GIVEN_KEY);
  const givenTo = Array.isArray(given) ? given : [];
  if (givenTo.includes(singer)) return [];

  storage.local.setItem(LEGACY_GIVEN_KEY, [...givenTo, singer]);
  return legacy;
};

// Songs starred before the phone had a singer go to the first singer it gets.
const claimUnnamedSongs = (singer: string) => {
  const legacy = takeLegacyList(singer);
  const lists = readLists();
  const carried = [...legacy, ...(lists[''] ?? [])];
  if (carried.length === 0) return;

  const { '': _unnamed, ...rest } = lists;
  writeLists({ ...rest, [singer]: [...new Set([...(lists[singer] ?? []), ...carried])] });
};

export const useMySongList = () => {
  const { name, hasStoredName } = useRemoteMicName();
  const singer = hasStoredName ? name.trim().toLowerCase() : '';
  const lists = useSyncExternalStore(subscribe, readLists);

  useEffect(() => {
    if (singer) claimUnnamedSongs(singer);
  }, [singer]);

  const savedSongList = lists[singer] ?? NO_SONGS;

  const toggleSong = useCallback(
    (songId: string) => {
      const current = readLists();
      const list = current[singer] ?? [];
      const saved = list.includes(songId);
      if (!saved) posthog.capture('remote-song-list-add', { songId });

      writeLists({ ...current, [singer]: saved ? list.filter((id) => id !== songId) : [...list, songId] });
    },
    [singer],
  );

  return { savedSongList, toggleSong, owner: hasStoredName ? name : null };
};
